import React, { useState, useEffect } from 'react';
import style from './style.module.css';

import { useParams } from 'react-router-dom';
import { useNotification } from '../../utils/notifications';

import countries from "i18n-iso-countries";

import { 
    getLanguageShortname, Language, 
    getLanguageArray, systemLanguage, getLabel,
    getOrganizations,
    getActiveOrganization, Organization,
    activeOrganization,
    getUuid, getToken, languagesList,
    parseUserLanguage,
    setOrganizations,
    setOrganizationCount,
    setUserCount
} from '../../utils/constants';

import { CreateUser, GetUser, EditUser } from '../../services/userApi';

import Loader from '../../assets/images/loader.svg';
import { CreateOrganization, GetOrganization, EditOrganization } from '../../services/organizationApi';

const OrganizationView: React.FC = () => {

    const uuid = getUuid()
    const token = getToken()

    const notify = useNotification()

    const { org_id, action } = useParams();

    const [sessionLang, setSessionLang] = useState<Language>(getLanguageArray());

    countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
    const countryObj = countries.getNames("en", { select: "official" });

    const [userLanguage, setUserLanguage] = useState<{ short: string | any } | false>(false);
    const user_language = getLanguageShortname();

    const [orgLoading,setOrgLoading] = useState<boolean>(true)
    const [viewType,setViewType] = useState<string>('create')

    const [isEditing,setIsEditing] = useState<boolean>(false)

    const [orgName, setOrgName] = useState<string>('')
    const [orgWebsite, setOrgWebsite] = useState<string>('')
    const [orgShortname, setOrgShortname] = useState<string>('')
    const [orgCountry, setOrgCountry] = useState<string>('')
    const [orgCity, setOrgCity] = useState<string>('')
    const [orgAddress, setOrgAddress] = useState<string>('')
    const [orgPostalCode, setOrgPostalCode] = useState<string>('')
    const [orgOwner,setOrgOwner] = useState<string>('')

    const [isSaving,setIsSaving] = useState<boolean>(false)

    const handleEditOrganization = () => {
        setIsEditing(true)
    }
    

    const handleSaveOrganization = async () => {
        setIsEditing(false)
        setIsSaving(true)

        const editOrganization = await EditOrganization({
            uuid: uuid,
            token: token, 
            organization_id: org_id,
            name: orgName,
            shortname: orgShortname,
            website: orgWebsite,
            country: orgCountry,
            address: orgAddress,
            postal_code: orgPostalCode,
            city: orgCity
        })

        notify('Saved Successfully', 3000)

        setOrganizations(JSON.stringify(editOrganization.organizations))
        setOrganizationCount(editOrganization.organizations_count)
        setUserCount(editOrganization.users_count)

        setIsEditing(false)
        setIsSaving(false)
    }

    const handleCreateUser = async () => {

        setIsSaving(true)

        const createOrganization = await CreateOrganization({
            uuid: uuid,
            token: token, 
            user_uuid: uuid, // -> future we should give admin ability to set
            name: orgName,
            shortname: orgShortname,
            website: orgWebsite,
            country: orgCountry,
            address: orgAddress,
            postal_code: orgPostalCode,
            city: orgCity
        })

        const organizationsList = getOrganizations()
        const newOrganizationsList = [...organizationsList, ...[createOrganization]];

        setOrganizations(JSON.stringify(newOrganizationsList))

        notify('Organization Created', 3000)
        setIsSaving(false)
    
    }



    const handleGetOrganizationInfo = async() => {
        const orgInfo = await GetOrganization({uuid: uuid, token: token, organization_id: org_id})

        setOrgName(orgInfo.name)
        setOrgShortname(orgInfo.shortname)
        setOrgWebsite(orgInfo.website)
        setOrgCountry(orgInfo.country)
        setOrgCity(orgInfo.city)
        setOrgAddress(orgInfo.address)
        setOrgPostalCode(orgInfo.postal_code)

        setOrgLoading(false)
    }

    useEffect(() => {
        if(org_id !== 'create')
        {
            setViewType('edit')
            if(action === 'edit')
            {
                setIsEditing(true)
            }
            handleGetOrganizationInfo()
            return
        }
        setIsEditing(true)
        setOrgLoading(false)
    }, [org_id])


    useEffect(() => {
        if (user_language) {
            setUserLanguage({ short: user_language.toUpperCase() });
        } else {
            setUserLanguage({ short: ((user_language as unknown) as Language).short || 'EN' });
        }
    }, [user_language]);

    useEffect(() => {
        window.scrollTo(0,0);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === systemLanguage && e.newValue) {
                setSessionLang(JSON.parse(e.newValue));
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    return (
        <div className={style.scoreboard}>
            <div className={style.header}>
                <div className={style.headerLeft}>
                    <div className={`${style.greeting} ${style.textContainerGrad}`}>
                        {/*getLabel(userLabel, sessionLang)*/}
                        {viewType === 'create' ? 'Create Organization' : orgLoading ? 'Loading...' : orgName}
                    </div>
                </div><div className={style.headerRight}>
                    {isEditing ? 
                        isSaving ? 
                            <img src={Loader} /> : 
                            <span className={style.save} onClick={viewType === 'create' ? handleCreateUser : handleSaveOrganization}>{viewType === 'create' ? 'Create' : 'Save' }</span> : 
                            <span onClick={handleEditOrganization}>Edit Organization</span>
                    }
                </div>
            </div>
            {
                !orgLoading ? 
                <>
                <div className={`${style.tableWrap} ${style.tableTopSpace}`}>
                    <div className={`${style.tableRow} ${style.header}`}>
                        <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.name}`}>
                            General Info
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Name</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={orgName} onChange={(e) => setOrgName(e.currentTarget.value)} /> : orgName}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Short Name</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={orgShortname} maxLength={5} onChange={(e) => setOrgShortname(e.currentTarget.value)} /> : orgShortname}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Website</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={orgWebsite} onChange={(e) => setOrgWebsite(e.currentTarget.value)} /> : orgWebsite}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${style.tableWrap} ${style.tableTopSpace}`}>
                    <div className={`${style.tableRow} ${style.header}`}>
                        <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.name}`}>
                            Location Information
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Country</div>
                                <div className={style.inputText}>{isEditing ? 
                                    <select value={orgCountry} onChange={(e) => setOrgCountry(e.target.value)}>
                                        <option value="">Select</option>
                                        {Object.entries(countryObj).map(([code, name]) => (
                                            <option key={code} value={code}>{name}</option>
                                        ))}
                                        </select>
                                : orgCountry}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>City</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={orgCity} onChange={(e) => setOrgCity(e.currentTarget.value)} /> : orgCity}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Address</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={orgAddress} onChange={(e) => setOrgAddress(e.currentTarget.value)} /> : orgAddress}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Postal Code</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={orgPostalCode} onChange={(e) => setOrgPostalCode(e.currentTarget.value)} /> : orgPostalCode}</div>
                            </div>
                        </div>
                    </div>
                </div>


                </> : ''
            }
        </div>
    );
}

export default OrganizationView;
